import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/use-auth';

/** Gates a route behind a session, remembering where the user was headed. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="grid min-h-svh place-items-center bg-canvas px-4 py-12 text-fg">
        <p role="status" className="flex items-center gap-2 text-sm text-fg-muted">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
            aria-hidden="true"
            className="h-4 w-4 shrink-0 animate-spin motion-reduce:animate-none"
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          Checking your session…
        </p>
      </div>
    );
  }

  /*
   * ⚠️ **`search` IS PART OF WHERE THEY WERE HEADED, AND LEAVING IT OUT LOSES
   * CREDENTIALS, NOT JUST CONVENIENCE.** *SUPERSEDED: `from: location.pathname`.*
   *
   * Every guarded route that carries state in the query string — a deep link
   * like `?cycleId=3`, a saved filter this app writes into the URL on arrival,
   * and above all **`/approve-company?token=…`, where the token IS the
   * credential** — came back from `/login` stripped to its bare path. The
   * approval page is the one where that is not a nuisance: the person signs in,
   * lands on a page with no token, and meets a screen that cannot resolve
   * anything. **No error, nothing in a log, and it would fire for every
   * approver arriving from an email rather than from an open tab** — which is
   * all of them.
   *
   * It is fixed HERE rather than at that one route because every other guarded
   * route had the same hole for the same reason; a per-page workaround would
   * leave the next deep link to rediscover it.
   *
   * ⚠️ **`LoginPage` does `<Navigate to={from} replace />`, so `from` must stay
   * a path-and-query it can navigate to and NOTHING ELSE.** It is built from
   * `location`, never from anything a caller supplies, so it cannot be pointed
   * off-site — keep it that way: an open redirect here would be handed a
   * signed-in session.
   */
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }
  return <>{children}</>;
}
